import { getLogger } from '@jitsi/logger';

import { JitsiConferenceEvents } from '../../react/features/base/lib-jitsi-meet';
import { MEDIA_TYPE, VIDEO_TYPE } from '../../react/features/base/media/constants';
import { CONNECTION_TYPE } from '../../react/features/base/premeeting/constants';

const logger = getLogger('MOSDetector');
const EXCELLENT_QUALITY = 100;
const BAD_QUALITY = 10;
const POOR_QUALITY = 5;

export default class MOSDetector {
    constructor(conference) {
        // Interval for collecting statistics (1 second)
        this.statsInterval = 1000;
        this.mosStatsIntervalId = null; // ID for interval timer
        this.activeMediaType = null; // Current media type (audio or video)
        this.isConfQualitySet = false; // Tracks if quality has been set
        this._conference = conference; // Reference to the conference instance
        this._tempProcessedStats = []; // Stores MOS statistics for sessions
        this.avgMOS = []; // Stores average MOS values

        this._conference.addEventListener(JitsiConferenceEvents.CONFERENCE_LEFT, this.dispose.bind(this));
        this._conference.addEventListener(JitsiConferenceEvents.TRACK_ADDED, this._onTrackAdded.bind(this));

        // Expose public method for retrieving MOS data
        conference.getMosData = this.getMosData.bind(this);
    }

    // Returns average MOS data calculated over the last 10 seconds
    getMosData() {
        return this._calculateAverageMOS10s() || {
            meanMOS: null,
            minMOS: null,
            meanMOSLast5s: null,
            mediaType: null
        };
    }

    // Finds the average MOS over all collected samples
    _findAverageUpload() {
        if (this.avgMOS.length === 0) {
            return 0;
        }

        return this.avgMOS.reduce((sum, currentValue) => sum + currentValue, 0) / this.avgMOS.length;
    }

    // Handles addition of a new track (audio or video)
    _onTrackAdded(track) {
        if (!track.isLocal()) {
            return;
        }

        // If audio track is added, prioritize it
        if (track.getType() === MEDIA_TYPE.AUDIO) {
            this.activeMediaType = MEDIA_TYPE.AUDIO;
            this._startCollectingMOSStats();
        } else if (
            track.videoType === VIDEO_TYPE.DESKTOP
            && this.activeMediaType !== MEDIA_TYPE.AUDIO
        ) {
            // If no audio, fall back to video
            this.activeMediaType = MEDIA_TYPE.VIDEO;
            this._startCollectingMOSStats();
        }
    }

    // Starts collecting MOS statistics
    _startCollectingMOSStats() {
        // Stop any existing stat collection first
        this._stopCollectingMOSStats();

        // Set up interval for periodic stat collection
        this.mosStatsIntervalId = setInterval(() => {
            if (!this.activeMediaType) {
                return;
            }

            const pc = this._conference.jvbJingleSession?.peerconnection;

            if (!pc) {
                return;
            }

            pc.getStats(null)
                .then(stats => {
                    // Select appropriate processing and calculation methods based on media type
                    const processStats = this.activeMediaType === MEDIA_TYPE.AUDIO
                        ? this._processAudioWebRTCStatsForMOS.bind(this)
                        : this._processVideoWebRTCStatsForMOS.bind(this);

                    const calculateScore = this.activeMediaType === MEDIA_TYPE.AUDIO
                        ? this.calculateAudioScore.bind(this)
                        : this.calculateVideoScore.bind(this);

                    const processedStats = processStats(stats);

                    if (!processedStats) {
                        return;
                    }

                    // RAW data from stats by type media
                    this._tempProcessedStats.push(processedStats);
                    const mos = calculateScore(this._tempProcessedStats);

                    // We will collect only small amount of array, for best performance for long conferences
                    if (this._tempProcessedStats.length > 2) {
                        this._tempProcessedStats.shift();
                    }

                    if (mos === -1) {
                        return;
                    }

                    this.avgMOS.push(mos);

                    if (this.avgMOS.length <= 10) {
                        return;
                    }

                    const avgUpload = this._findAverageUpload();

                    this._updateIconStats(avgUpload);

                    this.avgMOS.shift();

                })
                .catch(error => {
                    logger.error('Error getting peerConnection stats:', error);
                });
        }, this.statsInterval);
    }

    // Updates connection quality icon based on MOS score
    _updateIconStats(score) {
        if (score !== -1) {
            if (score >= 4.03) {
                this._updateStatsQuality(EXCELLENT_QUALITY);
            } else if (score >= 3.6) {
                this._updateStatsQuality(BAD_QUALITY);
            } else {
                this._updateStatsQuality(POOR_QUALITY);
            }
        }
    }

    // Updates connection quality in the UI and backend
    _updateStatsQuality(value) {
        if (value > 10) {
            if (this.isConfQualitySet) {
                console.warn('Restoring previous connection quality value: 100');
                this._conference.connectionQuality._updateLocalConnectionQuality(100);
                this._conference.connectionQuality._broadcastLocalStats();
                this.isConfQualitySet = false;
            }
        } else {
            this._conference.connectionQuality._updateLocalConnectionQuality(value);
            this._conference.connectionQuality._broadcastLocalStats();
            this.isConfQualitySet = true;
        }
    }

    // Processes WebRTC audio stats to extract MOS-related data
    _processAudioWebRTCStatsForMOS(stats) {
        // Parse audio-specific WebRTC stats and calculate necessary metrics
        let inboundRtpAudio = null;
        let outboundRtpAudio = null;
        let remoteInboundRtpAudio = null;
        let statsResult = null;

        stats.forEach(report => {
            if (report.type === 'inbound-rtp' && report.kind === 'audio') {
                inboundRtpAudio = report;
            }

            if (report.type === 'outbound-rtp' && report.kind === 'audio') {
                outboundRtpAudio = report;
            }
        });

        if (outboundRtpAudio && outboundRtpAudio.remoteId) {
            stats.forEach(report => {
                if (report.type === 'remote-inbound-rtp' && report.id === outboundRtpAudio.remoteId) {
                    remoteInboundRtpAudio = report;
                }
            });
        }

        if (inboundRtpAudio && remoteInboundRtpAudio) {
            const packetsLost = inboundRtpAudio.packetsLost || 0;
            const packetsReceived = inboundRtpAudio.packetsReceived || 0;

            // RTT from remote-inbound-rtp
            const rtt = remoteInboundRtpAudio.roundTripTime ? remoteInboundRtpAudio.roundTripTime * 1000 : 0; // in milliseconds

            statsResult = {
                audio: {
                    timestamp: inboundRtpAudio.timestamp,
                    packetsLost,
                    packetsReceived,
                    rtt
                }
            };
        }

        return statsResult;
    }

    // Calculates audio MOS score using RTT and packet loss rate
    calculateAudioScore(stats) {
        const audioScore = function(rtt, plr) {
            const LOCAL_DELAY = 20; // 20 msecs: typical frame duration

            function H(x) {
                return x < 0 ? 0 : 1;
            }

            const a = 0; // ILBC: a=10
            const b = 19.8;
            const c = 29.7;

            // R = 94.2 − Id − Ie
            const R = function(rtt, packetLoss) {
                const d = rtt + LOCAL_DELAY;
                const Id = 0.024 * d + 0.11 * (d - 177.3) * H(d - 177.3);

                const P = packetLoss;
                const Ie = a + b * Math.log(1 + c * P);

                return 94.2 - Id - Ie;
            };

            // For R < 0: MOS = 1
            // For 0 ≤ R ≤ 100: MOS = 1 + 0.035 R + 7.10E-6 R(R-60)(100-R)
            // For R > 100: MOS = 4.5
            const MOS = function(R) {
                if (R < 0) {
                    return 1;
                }
                if (R > 100) {
                    return 4.5;
                }

                return 1 + 0.035 * R + (R * (R - 60) * (100 - R)) * 7.10e-6;
            };

            return MOS(R(rtt, plr));
        };

        if (stats.length < 2) {
            return -1;
        }

        const currentStats = stats[stats.length - 1];
        const lastStats = stats[stats.length - 2];

        // Ensure audio stats exist
        if (!currentStats.audio || !lastStats.audio) {
            return -1;
        }

        const packetsLostDelta = currentStats.audio.packetsLost - lastStats.audio.packetsLost;
        const packetsReceivedDelta = currentStats.audio.packetsReceived - lastStats.audio.packetsReceived;
        const totalAudioPackets = packetsLostDelta + packetsReceivedDelta;

        // If totalAudioPackets is 0, set plr to 0 and proceed to calculate MOS using only RTT
        let plr = 0;

        if (totalAudioPackets !== 0) {
            plr = packetsLostDelta / totalAudioPackets;
        }

        const rtt = currentStats.audio.rtt;

        return audioScore(rtt, plr);
    }

    // Processes WebRTC video stats to extract MOS-related data
    _processVideoWebRTCStatsForMOS(stats) {
        const outboundRtpVideos = [];
        const mediaSources = {};
        let chosenOutbound = null;
        let bestResolutionScore = 0; // width * height

        // Collect all outbound video stats
        stats.forEach(report => {
            if (report.type === 'outbound-rtp' && report.kind === 'video') {
                outboundRtpVideos.push(report);
            }
            if (report.type === 'media-source' && report.kind === 'video') {
                mediaSources[report.id] = report;
            }
        });

        // If no outbound streams found, return null early
        if (outboundRtpVideos.length === 0) {
            return null;
        }

        // Pick the best outbound stream (e.g., highest resolution layer)
        for (const outbound of outboundRtpVideos) {
            let width = 0;
            let height = 0;

            if (outbound.mediaSourceId && mediaSources[outbound.mediaSourceId]) {
                const ms = mediaSources[outbound.mediaSourceId];

                width = ms.width || 0;
                height = ms.height || 0;
            }

            const resolutionScore = width * height;

            if (resolutionScore > bestResolutionScore) {
                bestResolutionScore = resolutionScore;
                chosenOutbound = outbound;
            }
        }

        if (!chosenOutbound) {
            return null;
        }

        // Attempt to find matching remote-inbound-rtp
        let remoteInboundRtpVideo = null;

        if (chosenOutbound.remoteId) {
            stats.forEach(report => {
                if (report.type === 'remote-inbound-rtp' && report.id === chosenOutbound.remoteId) {
                    remoteInboundRtpVideo = report;
                }
            });
        }

        // Lookup media source info for chosen outbound
        let chosenMediaSource = null;

        if (chosenOutbound.mediaSourceId && mediaSources[chosenOutbound.mediaSourceId]) {
            chosenMediaSource = mediaSources[chosenOutbound.mediaSourceId];
        }

        // Extract stats
        const packetsSent = chosenOutbound.packetsSent || 0;
        let packetsLost = 0;
        let rtt = (chosenOutbound.roundTripTime || 0) * 1000;

        if (remoteInboundRtpVideo) {
            packetsLost = remoteInboundRtpVideo.packetsLost || 0;
            rtt = (remoteInboundRtpVideo.roundTripTime || 0) * 1000;
        } else {
            // If no remote stats, some browsers may not provide packetsLost outbound.
            // Use outbound stats if available (not all browsers do).
            if (typeof chosenOutbound.packetsLost === 'number') {
                packetsLost = chosenOutbound.packetsLost;
            }
        }

        let resolution = { width: 320,
            height: 180 }; // fallback
        let frameRate = 5; // fallback

        if (chosenMediaSource) {
            resolution = {
                width: chosenMediaSource.width || 320,
                height: chosenMediaSource.height || 180
            };
            frameRate = chosenMediaSource.framesPerSecond || 5;
        }

        // If resolution or framerate is zero, fallback to defaults
        if (resolution.width === 0 || resolution.height === 0) {
            resolution = { width: 320,
                height: 180 };
        }
        if (frameRate === 0) {
            frameRate = 5;
        }

        return {
            video: {
                packetsLost,
                packetsSent,
                rtt,
                resolution,
                frameRate
            }
        };
    }
    calculateVideoScore(stats) {
        if (stats.length < 2) {
            return -1;
        }

        const currentStats = stats[stats.length - 1];
        const lastStats = stats[stats.length - 2];

        // Calculate deltas for packets lost and sent
        const packetsLostDelta = currentStats.video.packetsLost - lastStats.video.packetsLost;
        const packetsSentDelta = currentStats.video.packetsSent - lastStats.video.packetsSent;
        const totalPackets = packetsLostDelta + packetsSentDelta;

        let plr = 0;

        if (totalPackets > 0) {
            plr = packetsLostDelta / totalPackets;
        }

        const rtt = currentStats.video.rtt || 0;
        const resolution = currentStats.video.resolution || { width: 0,
            height: 0 };
        const frameRate = currentStats.video.frameRate || 0;

        // Normalize resolution factor: Full HD (1920x1080) = 1.0
        const resolutionFactor = Math.min(
            (resolution.width * resolution.height) / (1920 * 1080),
            1
        );

        // Normalize frame rate factor: 60fps = 1.0
        const frameRateFactor = Math.min(frameRate / 60, 1);

        return this._calculateVideoMOS(rtt, plr, resolutionFactor, frameRateFactor);
    }
    _calculateVideoMOS(rtt, plr, resolutionFactor = 1, frameRateFactor = 1) {
        const LOCAL_DELAY = 20;

        // Fine-tuned Delay Impairment (Id)
        const d = rtt + LOCAL_DELAY;
        const Id = 0.0265 * d + 0.11 * Math.max(0, d - 177.3);

        // Equipment Impairment (Ie)
        const a = 0;
        const b = 25;
        const c = 29.7;
        const Ie = a + b * Math.log(1 + c * plr);

        // Penalties
        const resolutionPenalty = (1 - resolutionFactor) * 5;
        const frameRatePenalty = (1 - frameRateFactor) * 3;

        // R-value calculation
        const R = 94.2 - Id - Ie - resolutionPenalty - frameRatePenalty;

        // MOS Mapping Function
        if (R < 0) {
            return 1.0;
        }
        if (R > 100) {
            return 4.5;
        }

        return (
            1
            + 0.035 * R
            + ((R * (R - 60) * (100 - R)) * 7.10e-6)
        );
    }
    _calculateAverageMOS10s() {
        const mosScores = this.avgMOS.filter(score => score !== -1);

        if (mosScores.length === 0) {
            return null;
        }

        const minMOS10s = Math.min(...mosScores);
        const avgMOS10s = this._findAverageUpload(); // Reuse the function here

        return {
            minMOS10s,
            avgMOS10s,
            mediaType: this.activeMediaType,
            MOSStatus: this._getMOSStatus(avgMOS10s)
        };
    }

    _getMOSStatus(score) {
        let mosResult = '';

        if (score >= 4.03) {
            mosResult = CONNECTION_TYPE.GOOD;
        } else if (score >= 3.6) {
            mosResult = CONNECTION_TYPE.NON_OPTIMAL;
        } else {
            mosResult = CONNECTION_TYPE.POOR;
        }

        return mosResult;
    }

    _stopCollectingMOSStats() {
        if (this.mosStatsIntervalId) {
            clearInterval(this.mosStatsIntervalId);
            this.mosStatsIntervalId = null;
        }
    }

    // Clears internal data buffers
    _clearData() {
        this.isConfQualitySet = false;
        this._tempProcessedStats = [];
        this.avgMOS = [];
    }

    // Cleans up resources and disposes of the detector
    dispose() {
        this._clearData();
        this._stopCollectingMOSStats();
    }
}
